import { useState, useEffect, Fragment } from "react";
import { Loader2, Trash2, ExternalLink, Download, Upload, Star, Share2, Sparkles, ChevronDown, Check, Eye, Minus, Plus, Trophy, Users, UserPlus, X, Pencil, KeyRound, UserCheck, RotateCw } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { FileLink } from "@/components/FileLink";
import { PdfUpload } from "@/components/PdfUpload";
import { ConfirmDialog } from "@/components/ConfirmDialog";

const inputCls = "bg-[#050505] border border-[#2A2A2A] px-3 py-2 text-white text-xs focus:outline-none focus:border-[#FF4D00]";

const STATES = [
  { id: "under_review", label: "Under Review" },
  { id: "approved_for_onboarding", label: "Approved for Onboarding" },
  { id: "onboarding_submitted", label: "Onboarding Submitted" },
  { id: "fully_approved", label: "Fully Approved" },
];
const stateLabel = (id) => STATES.find((s) => s.id === id)?.label || id;

export default function LeadsTab({ refreshStats }) {
  const [leads, setLeads] = useState(null);
  const [counts, setCounts] = useState({});
  const [expertiseOpts, setExpertiseOpts] = useState([]);
  const [fState, setFState] = useState("");
  const [fShort, setFShort] = useState(false);
  const [fExpertise, setFExpertise] = useState("");
  const [openId, setOpenId] = useState(null);
  const [selected, setSelected] = useState([]);
  const [rosterBusy, setRosterBusy] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [editLead, setEditLead] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = async () => {
    const params = {};
    if (fState) params.state = fState;
    if (fExpertise) params.expertise = fExpertise;
    if (fShort) params.shortlisted = true;
    const { data } = await api.get("/admin/leads", { params });
    setLeads(data.leads);
    setCounts(data.counts || {});
  };
  useEffect(() => { api.get("/expertise").then(({ data }) => setExpertiseOpts(data.expertise)).catch(() => {}); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [fState, fShort, fExpertise]);

  const setState = async (id, state) => {
    try { await api.post(`/admin/leads/${id}/state`, { state }); toast.success(`Moved to ${stateLabel(state)} — email sent`); load(); refreshStats && refreshStats(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const toggleStar = async (l) => {
    try { await api.post(`/admin/leads/${l.id}/shortlist`, { shortlisted: !l.shortlisted }); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const del = async (l) => {
    try { await api.delete(`/admin/leads/${l.id}`); toast.success("Creative deleted"); setConfirmDel(null); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const sendLogin = async (l) => {
    try { await api.post(`/admin/leads/${l.id}/create-account`); toast.success(`Login account created — temp password emailed to ${l.email}`); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const resendLogin = async (l) => {
    try { await api.post(`/admin/leads/${l.id}/resend-credentials`); toast.success(`New temp password emailed to ${l.email}`); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const share = async (l) => {
    try {
      const { data } = await api.get(`/admin/leads/${l.id}/talent-link`);
      await navigator.clipboard.writeText(data.url);
      const noHighlights = !l.highlight1 && !l.highlight2 && !l.highlight3;
      toast.success(noHighlights ? "Link copied — tip: add highlights first for a richer card" : "Talent card link copied to clipboard");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not copy link"); }
  };
  const setJobs = async (l, delta) => {
    try { await api.post(`/admin/leads/${l.id}/completed-jobs`, { delta }); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const toggleSelect = (id) => setSelected((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const generateRoster = async () => {
    if (!selected.length) return;
    setRosterBusy(true);
    try {
      const { data } = await api.post("/admin/roster-link", { lead_ids: selected });
      await navigator.clipboard.writeText(data.url);
      toast.success(`Roster link for ${selected.length} creative(s) copied to clipboard`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not create roster link"); }
    finally { setRosterBusy(false); }
  };

  const chip = (label, active, count, onClick, testId) => (
    <button key={testId} data-testid={testId} onClick={onClick}
      className={`inline-flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-[0.1em] border transition-colors ${active ? "border-[#FF4D00] bg-[#FF4D0020] text-white" : "border-[#2A2A2A] text-[#A1A1AA] hover:border-[#FF4D00]"}`}>
      {label} <span className={`font-bebas text-base leading-none ${active ? "text-[#FF4D00]" : "text-white"}`}>{count ?? 0}</span>
    </button>
  );

  if (!leads) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#FF4D00]" /></div>;

  return (
    <div className="space-y-8" data-testid="leads-tab">
      <TemplatesManager />

      <div>
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <h3 className="font-bebas text-2xl tracking-wide">CREATIVE LEADS PIPELINE</h3>
          <div className="flex items-center gap-3 ml-auto" data-testid="leads-actions">
            {selected.length > 0 && (
              <div className="flex items-center gap-3" data-testid="roster-bar">
                <span className="text-xs text-[#A1A1AA]">{selected.length} selected</span>
                <button data-testid="generate-roster-btn" onClick={generateRoster} disabled={rosterBusy}
                  className="inline-flex items-center gap-2 bg-[#FF4D00] text-white px-4 py-2 text-xs hover:bg-[#E64500] transition-colors disabled:opacity-60">
                  {rosterBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Users className="w-3.5 h-3.5" />} Generate Roster Link
                </button>
                <button data-testid="clear-roster-btn" onClick={() => setSelected([])} className="text-xs text-[#A1A1AA] hover:text-white transition-colors">Clear</button>
              </div>
            )}
            <button data-testid="add-creative-btn" onClick={() => setShowAdd(true)}
              className="inline-flex items-center gap-2 border border-[#FF4D00] text-[#FF4D00] px-4 py-2 text-xs uppercase tracking-[0.1em] hover:bg-[#FF4D0020] transition-colors">
              <UserPlus className="w-3.5 h-3.5" /> Add Creative
            </button>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-4" data-testid="leads-stage-badges">
          {chip("All", !fState && !fShort, counts.all, () => { setFState(""); setFShort(false); }, "stage-chip-all")}
          {STATES.map((s) => chip(s.label, fState === s.id && !fShort, counts[s.id], () => { setFShort(false); setFState(fState === s.id ? "" : s.id); }, `stage-chip-${s.id}`))}
          <button data-testid="stage-chip-shortlisted" onClick={() => { setFState(""); setFShort((v) => !v); }}
            className={`inline-flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-[0.1em] border transition-colors ${fShort ? "border-[#FF4D00] bg-[#FF4D0020] text-white" : "border-[#2A2A2A] text-[#A1A1AA] hover:border-[#FF4D00]"}`}>
            <Star className={`w-3.5 h-3.5 ${fShort ? "text-[#FF4D00] fill-[#FF4D00]" : ""}`} /> Shortlisted <span className="font-bebas text-base leading-none text-[#FF4D00]">{counts.shortlisted ?? 0}</span>
          </button>
          <select data-testid="leads-filter-expertise" className={`${inputCls} ml-auto`} value={fExpertise} onChange={(e) => setFExpertise(e.target.value)}>
            <option value="">All expertise</option>
            {expertiseOpts.map((o) => <option key={o.id} value={o.name}>{o.name}</option>)}
          </select>
        </div>

        {!leads.length ? (
          <div className="border border-dashed border-[#2A2A2A] p-12 text-center text-sm text-[#A1A1AA]">No leads match these filters.</div>
        ) : (
          <div className="border border-[#2A2A2A] leads-table-wrap" data-testid="leads-table">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-[#2A2A2A] text-left text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA]">
                <th className="p-4 w-8"></th><th className="p-4 w-8"></th><th className="p-4">Name</th><th className="p-4">Email</th><th className="p-4">Expertise</th><th className="p-4">Jobs</th><th className="p-4">Views</th><th className="p-4">Reel</th><th className="p-4">Docs</th><th className="p-4">State</th><th className="p-4">Move to</th><th className="p-4">Talent Card</th><th className="p-4"></th>
              </tr></thead>
              <tbody>
                {leads.map((l) => (
                  <Fragment key={l.id}>
                    <tr className="border-b border-[#2A2A2A] hover:bg-[#111111] transition-colors align-top" data-testid={`lead-row-${l.id}`}>
                      <td className="p-4">
                        <input type="checkbox" data-testid={`lead-select-${l.id}`} checked={selected.includes(l.id)} onChange={() => toggleSelect(l.id)}
                          className="w-4 h-4 accent-[#FF4D00] cursor-pointer" title="Select for roster" />
                      </td>
                      <td className="p-4">
                        <button data-testid={`lead-star-${l.id}`} onClick={() => toggleStar(l)} title="Shortlist">
                          <Star className={`w-5 h-5 transition-colors ${l.shortlisted ? "text-[#FF4D00] fill-[#FF4D00]" : "text-[#555] hover:text-[#FF4D00]"}`} />
                        </button>
                      </td>
                      <td className="p-4 text-white whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          {(l.completed_jobs || 0) >= 5 && <Trophy className="w-4 h-4 text-[#FF4D00]" title="All-Star" data-testid={`allstar-${l.id}`} />}
                          {l.name}
                        </div>
                        {l.user_id && <AccountBadge lead={l} />}
                      </td>
                      <td className="p-4 text-[#A1A1AA]"><span className="block max-w-[160px] truncate">{l.email}</span></td>
                      <td className="p-4">
                        <div className="flex flex-wrap gap-1 max-w-[14rem]">
                          {(l.expertise || []).map((x) => <span key={x} className="text-[10px] uppercase tracking-wide border border-[#2A2A2A] text-[#A1A1AA] px-1.5 py-0.5">{x === "Other" && l.expertise_other ? l.expertise_other : x}</span>)}
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-1.5">
                          <button data-testid={`jobs-dec-${l.id}`} onClick={() => setJobs(l, -1)} disabled={(l.completed_jobs || 0) === 0} className="border border-[#2A2A2A] text-[#A1A1AA] p-1 hover:bg-[#1A1A1A] hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed"><Minus className="w-3 h-3" /></button>
                          <span data-testid={`jobs-count-${l.id}`} className="font-bebas text-xl text-white w-6 text-center leading-none">{l.completed_jobs || 0}</span>
                          <button data-testid={`jobs-inc-${l.id}`} onClick={() => setJobs(l, 1)} className="border border-[#2A2A2A] text-[#A1A1AA] p-1 hover:bg-[#1A1A1A] hover:text-white transition-colors"><Plus className="w-3 h-3" /></button>
                        </div>
                      </td>
                      <td className="p-4">
                        <span data-testid={`views-count-${l.id}`} className="inline-flex items-center gap-1.5 text-sm text-[#A1A1AA]"><Eye className="w-4 h-4" /> {l.view_count || 0}</span>
                      </td>
                      <td className="p-4">{l.reel_url ? <a href={l.reel_url} target="_blank" rel="noreferrer" className="text-[#FF4D00] hover:underline inline-flex items-center gap-1 text-xs"><ExternalLink className="w-3 h-3" /> Reel</a> : <span className="text-xs text-[#555]">—</span>}</td>
                      <td className="p-4">
                        <div className="flex flex-col gap-1">
                          {l.resume_file_id && <FileLink fileId={l.resume_file_id} testId={`lead-resume-${l.id}`} className="text-[#FF4D00] hover:underline inline-flex items-center gap-1 text-xs"><Download className="w-3 h-3" /> Resume</FileLink>}
                          {l.w9_file_id && <FileLink fileId={l.w9_file_id} testId={`lead-w9-${l.id}`} className="text-[#FF4D00] hover:underline inline-flex items-center gap-1 text-xs"><Download className="w-3 h-3" /> W-9</FileLink>}
                          {l.noncompete_file_id && <FileLink fileId={l.noncompete_file_id} testId={`lead-nc-${l.id}`} className="text-[#FF4D00] hover:underline inline-flex items-center gap-1 text-xs"><Download className="w-3 h-3" /> Non-Compete</FileLink>}
                          {l.conduct_file_id && <FileLink fileId={l.conduct_file_id} testId={`lead-cp-${l.id}`} className="text-[#FF4D00] hover:underline inline-flex items-center gap-1 text-xs"><Download className="w-3 h-3" /> Conduct</FileLink>}
                        </div>
                      </td>
                      <td className="p-4"><span className="text-xs uppercase tracking-[0.1em] text-[#FF4D00] whitespace-nowrap">{stateLabel(l.state)}</span></td>
                      <td className="p-4">
                        <select data-testid={`lead-state-${l.id}`} className={`${inputCls} w-[100px]`} value={l.state} onChange={(e) => setState(l.id, e.target.value)}>
                          {STATES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                        </select>
                      </td>
                      <td className="p-4 pr-2">
                        <div className="flex items-center gap-2">
                          <button data-testid={`lead-highlights-toggle-${l.id}`} onClick={() => setOpenId(openId === l.id ? null : l.id)} className="inline-flex items-center gap-1 border border-[#2A2A2A] text-white px-2 py-1.5 text-xs hover:bg-[#1A1A1A] transition-colors"><Sparkles className="w-3.5 h-3.5" /> Highlights <ChevronDown className={`w-3 h-3 transition-transform ${openId === l.id ? "rotate-180" : ""}`} /></button>
                          <button data-testid={`lead-share-${l.id}`} onClick={() => share(l)} className="inline-flex items-center gap-1 bg-[#FF4D00] text-white px-2 py-1.5 text-xs hover:bg-[#E64500] transition-colors"><Share2 className="w-3.5 h-3.5" /> Share</button>
                        </div>
                      </td>
                      <td className="py-4 pl-2 pr-3">
                        <div className="flex items-center gap-1.5">
                          {l.state === "fully_approved" && !l.user_id && (
                            <button data-testid={`lead-send-login-${l.id}`} onClick={() => sendLogin(l)} title="Create login account & email temp password" className="border border-[#10B981] text-[#10B981] px-2 py-1 hover:bg-[#10B98120] transition-colors"><KeyRound className="w-4 h-4" /></button>
                          )}
                          {l.account_status === "invite_sent" && <ResendButton lead={l} onResend={() => resendLogin(l)} />}
                          <button data-testid={`lead-edit-${l.id}`} onClick={() => setEditLead(l)} title="Edit creative" className="border border-[#2A2A2A] text-white px-2 py-1 hover:bg-[#1A1A1A] transition-colors"><Pencil className="w-4 h-4" /></button>
                          <button data-testid={`lead-delete-${l.id}`} onClick={() => setConfirmDel(l)} title="Delete creative" className="border border-[#EF4444] text-[#EF4444] px-2 py-1 hover:bg-[#EF444420] transition-colors"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </td>
                    </tr>
                    {openId === l.id && (
                      <tr key={`${l.id}-hl`} className="border-b border-[#2A2A2A] bg-[#0A0A0A]">
                        <td colSpan={13} className="p-5"><HighlightsEditor lead={l} onSaved={load} /></td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showAdd && (
        <AddCreativeModal
          expertiseOpts={expertiseOpts}
          onClose={() => setShowAdd(false)}
          onCreated={() => { setShowAdd(false); load(); refreshStats && refreshStats(); }}
        />
      )}
      {editLead && (
        <EditCreativeModal
          lead={editLead}
          expertiseOpts={expertiseOpts}
          onClose={() => setEditLead(null)}
          onSaved={() => { setEditLead(null); load(); refreshStats && refreshStats(); }}
        />
      )}
      {confirmDel && (
        <ConfirmDialog
          title="Delete Creative"
          message={`Permanently delete ${confirmDel.name}? They will be removed from the pipeline and any generated talent cards will stop working. This cannot be undone.`}
          confirmLabel="Delete Creative"
          onCancel={() => setConfirmDel(null)}
          onConfirm={() => del(confirmDel)}
        />
      )}
    </div>
  );
}

function AddCreativeModal({ expertiseOpts, onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", email: "", reel_url: "", highlight1: "", highlight2: "", highlight3: "", direct_to_vetted: true });
  const [exp, setExp] = useState([]);
  const [otherText, setOtherText] = useState("");
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleExp = (name) => setExp((p) => (p.includes(name) ? p.filter((x) => x !== name) : [...p, name]));
  const submit = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    if (!form.email.trim()) return toast.error("Email is required");
    setSaving(true);
    try {
      const { data } = await api.post("/admin/leads", { ...form, expertise: exp, expertise_other: exp.includes("Other") ? otherText : "" });
      toast.success(data.state === "fully_approved" ? "Creative added — Fully Approved (Vetted)" : "Creative added — Under Review");
      onCreated();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not add creative"); }
    finally { setSaving(false); }
  };
  const chips = [...expertiseOpts.map((o) => o.name), "Other"];
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm" data-testid="add-creative-modal" onMouseDown={onClose}>
      <div className="w-full max-w-lg max-h-[90vh] overflow-y-auto bg-[#0A0A0A] border border-[#2A2A2A] shadow-[0_0_60px_rgba(255,77,0,0.15)]" onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-[#2A2A2A] p-5">
          <div className="flex items-center gap-2"><UserPlus className="w-4 h-4 text-[#FF4D00]" /><h3 className="font-bebas text-2xl tracking-wide">ADD CREATIVE</h3></div>
          <button data-testid="ac-close" onClick={onClose} className="text-[#A1A1AA] hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-5">
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] mb-1.5">Name</label>
            <input data-testid="ac-name" className={`${inputCls} w-full`} maxLength={200} value={form.name} onChange={(e) => upd("name", e.target.value)} placeholder="Full name" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] mb-1.5">Email</label>
            <input data-testid="ac-email" type="email" className={`${inputCls} w-full`} value={form.email} onChange={(e) => upd("email", e.target.value)} placeholder="name@email.com" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] mb-1.5">Expertise</label>
            <div className="flex flex-wrap gap-2">
              {chips.map((name) => (
                <button key={name} type="button" data-testid={`ac-exp-${name}`} onClick={() => toggleExp(name)}
                  className={`text-[11px] uppercase tracking-wide px-2.5 py-1.5 border transition-colors ${exp.includes(name) ? "border-[#FF4D00] bg-[#FF4D0020] text-white" : "border-[#2A2A2A] text-[#A1A1AA] hover:border-[#FF4D00]"}`}>{name}</button>
              ))}
            </div>
            {exp.includes("Other") && (
              <input data-testid="ac-other" className={`${inputCls} w-full mt-2`} maxLength={200} value={otherText} onChange={(e) => setOtherText(e.target.value)} placeholder="Specify other expertise" />
            )}
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] mb-1.5">Reel URL (optional)</label>
            <input data-testid="ac-reel" className={`${inputCls} w-full`} maxLength={500} value={form.reel_url} onChange={(e) => upd("reel_url", e.target.value)} placeholder="https://…" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] mb-1.5">Professional Highlights (optional)</label>
            <div className="space-y-2">
              {[1, 2, 3].map((n) => (
                <input key={n} data-testid={`ac-highlight-${n}`} className={`${inputCls} w-full`} maxLength={200} value={form[`highlight${n}`]} onChange={(e) => upd(`highlight${n}`, e.target.value)} placeholder={`Highlight ${n}`} />
              ))}
            </div>
          </div>
          <button type="button" data-testid="ac-direct-toggle" onClick={() => upd("direct_to_vetted", !form.direct_to_vetted)}
            className="flex items-center justify-between w-full border border-[#2A2A2A] p-3 hover:border-[#FF4D00] transition-colors">
            <span className="text-left">
              <span className="block text-xs text-white uppercase tracking-[0.1em]">Direct to Vetted</span>
              <span className="block text-[11px] text-[#A1A1AA] mt-0.5">Skip review — land directly in Fully Approved</span>
            </span>
            <span className={`relative w-11 h-6 rounded-full transition-colors ${form.direct_to_vetted ? "bg-[#FF4D00]" : "bg-[#2A2A2A]"}`}>
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${form.direct_to_vetted ? "translate-x-5" : ""}`} />
            </span>
          </button>
        </div>
        <div className="flex items-center justify-end gap-3 border-t border-[#2A2A2A] p-5">
          <button data-testid="ac-cancel" onClick={onClose} className="text-xs text-[#A1A1AA] hover:text-white px-3 py-2">Cancel</button>
          <button data-testid="ac-submit" onClick={submit} disabled={saving}
            className="inline-flex items-center gap-2 bg-[#FF4D00] text-white px-5 py-2.5 text-xs uppercase tracking-[0.1em] hover:bg-[#E64500] transition-colors disabled:opacity-60">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Add Creative
          </button>
        </div>
      </div>
    </div>
  );
}

function EditCreativeModal({ lead, expertiseOpts, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: lead.name || "", email: lead.email || "", reel_url: lead.reel_url || "",
    highlight1: lead.highlight1 || "", highlight2: lead.highlight2 || "", highlight3: lead.highlight3 || "",
  });
  const [exp, setExp] = useState(lead.expertise || []);
  const [otherText, setOtherText] = useState(lead.expertise_other || "");
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleExp = (name) => setExp((p) => (p.includes(name) ? p.filter((x) => x !== name) : [...p, name]));
  const submit = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    if (!form.email.trim()) return toast.error("Email is required");
    setSaving(true);
    try {
      await api.put(`/admin/leads/${lead.id}`, { ...form, expertise: exp, expertise_other: exp.includes("Other") ? otherText : "" });
      toast.success("Creative updated");
      onSaved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not update creative"); }
    finally { setSaving(false); }
  };
  const chips = [...expertiseOpts.map((o) => o.name), "Other"];
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm" data-testid="edit-creative-modal" onMouseDown={onClose}>
      <div className="w-full max-w-lg max-h-[90vh] overflow-y-auto bg-[#0A0A0A] border border-[#2A2A2A] shadow-[0_0_60px_rgba(255,77,0,0.15)]" onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-[#2A2A2A] p-5">
          <div className="flex items-center gap-2"><Pencil className="w-4 h-4 text-[#FF4D00]" /><h3 className="font-bebas text-2xl tracking-wide">EDIT CREATIVE</h3></div>
          <button data-testid="ec-close" onClick={onClose} className="text-[#A1A1AA] hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-5">
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] mb-1.5">Name</label>
            <input data-testid="ec-name" className={`${inputCls} w-full`} maxLength={200} value={form.name} onChange={(e) => upd("name", e.target.value)} placeholder="Full name" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] mb-1.5">Email</label>
            <input data-testid="ec-email" type="email" className={`${inputCls} w-full`} value={form.email} onChange={(e) => upd("email", e.target.value)} placeholder="name@email.com" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] mb-1.5">Expertise</label>
            <div className="flex flex-wrap gap-2">
              {chips.map((name) => (
                <button key={name} type="button" data-testid={`ec-exp-${name}`} onClick={() => toggleExp(name)}
                  className={`text-[11px] uppercase tracking-wide px-2.5 py-1.5 border transition-colors ${exp.includes(name) ? "border-[#FF4D00] bg-[#FF4D0020] text-white" : "border-[#2A2A2A] text-[#A1A1AA] hover:border-[#FF4D00]"}`}>{name}</button>
              ))}
            </div>
            {exp.includes("Other") && (
              <input data-testid="ec-other" className={`${inputCls} w-full mt-2`} maxLength={200} value={otherText} onChange={(e) => setOtherText(e.target.value)} placeholder="Specify other expertise" />
            )}
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] mb-1.5">Reel URL (optional)</label>
            <input data-testid="ec-reel" className={`${inputCls} w-full`} maxLength={500} value={form.reel_url} onChange={(e) => upd("reel_url", e.target.value)} placeholder="https://…" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] mb-1.5">Professional Highlights (optional)</label>
            <div className="space-y-2">
              {[1, 2, 3].map((n) => (
                <input key={n} data-testid={`ec-highlight-${n}`} className={`${inputCls} w-full`} maxLength={200} value={form[`highlight${n}`]} onChange={(e) => upd(`highlight${n}`, e.target.value)} placeholder={`Highlight ${n}`} />
              ))}
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 border-t border-[#2A2A2A] p-5">
          <button data-testid="ec-cancel" onClick={onClose} className="text-xs text-[#A1A1AA] hover:text-white px-3 py-2">Cancel</button>
          <button data-testid="ec-submit" onClick={submit} disabled={saving}
            className="inline-flex items-center gap-2 bg-[#FF4D00] text-white px-5 py-2.5 text-xs uppercase tracking-[0.1em] hover:bg-[#E64500] transition-colors disabled:opacity-60">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Save changes
          </button>
        </div>
      </div>
    </div>
  );
}

function HighlightsEditor({ lead, onSaved }) {
  const [h, setH] = useState({ highlight1: lead.highlight1 || "", highlight2: lead.highlight2 || "", highlight3: lead.highlight3 || "" });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try { await api.put(`/admin/leads/${lead.id}/highlights`, h); toast.success("Highlights saved"); onSaved(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <div data-testid={`highlights-editor-${lead.id}`}>
      <div className="text-[10px] uppercase tracking-[0.25em] text-[#FF4D00] mb-3">Professional Summary — shown on the public talent card</div>
      <div className="grid md:grid-cols-3 gap-3">
        {[1, 2, 3].map((n) => (
          <input key={n} data-testid={`highlight-input-${lead.id}-${n}`} className={`${inputCls} w-full`} maxLength={200}
            placeholder={`Highlight ${n}`} value={h[`highlight${n}`]} onChange={(e) => setH({ ...h, [`highlight${n}`]: e.target.value })} />
        ))}
      </div>
      <button data-testid={`highlights-save-${lead.id}`} onClick={save} disabled={saving} className="mt-3 inline-flex items-center gap-2 bg-[#FF4D00] text-white px-4 py-2 text-xs hover:bg-[#E64500] transition-colors disabled:opacity-60">
        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Save highlights
      </button>
    </div>
  );
}

const SLOTS = [
  { key: "w9_file_id", cat: "template", label: "W-9 Template" },
  { key: "noncompete_file_id", cat: "template", label: "Non-Compete Template" },
  { key: "conduct_file_id", cat: "template", label: "Conduct & Policy Template" },
];

function TemplatesManager() {
  const [tpl, setTpl] = useState(null);
  const [pending, setPending] = useState({});

  useEffect(() => { api.get("/admin/onboarding-templates").then(({ data }) => setTpl(data)).catch(() => setTpl({})); }, []);

  const saveSlot = async (key, fileId) => {
    try { await api.put("/admin/onboarding-templates", { [key]: fileId }); setTpl((t) => ({ ...t, [key]: fileId })); toast.success("Template saved"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (!tpl) return null;
  return (
    <div className="border border-[#2A2A2A] bg-[#111111] p-6" data-testid="templates-manager">
      <div className="flex items-center gap-2 mb-1"><Upload className="w-4 h-4 text-[#FF4D00]" /><h3 className="font-bebas text-2xl tracking-wide">ONBOARDING TEMPLATES</h3></div>
      <p className="text-sm text-[#A1A1AA] mb-5">Upload the blank PDF forms approved creatives download, sign, and return.</p>
      <div className="grid md:grid-cols-3 gap-4">
        {SLOTS.map((s) => (
          <div key={s.key}>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] mb-1 flex items-center justify-between">
              <span>{s.label}</span>
              {tpl[s.key] && <span className="text-[#10B981]">Set</span>}
            </div>
            <PdfUpload testId={`template-upload-${s.key}`} category={s.cat} value={pending[s.key]}
              onUploaded={(d) => { setPending((p) => ({ ...p, [s.key]: d })); saveSlot(s.key, d.id); }}
              label={tpl[s.key] ? "Replace PDF" : "Upload PDF"} />
          </div>
        ))}
      </div>
    </div>
  );
}

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "");

function ResendButton({ lead, onResend }) {
  const [busy, setBusy] = useState(false);
  const click = async () => { setBusy(true); await onResend(); setBusy(false); };
  return (
    <button data-testid={`lead-resend-login-${lead.id}`} onClick={click} disabled={busy} title="Resend credentials (new temp password)"
      className="border border-[#F59E0B] text-[#F59E0B] px-1.5 py-1 hover:bg-[#F59E0B20] transition-colors disabled:opacity-40">
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCw className="w-4 h-4" />}
    </button>
  );
}

function AccountBadge({ lead }) {
  const pending = lead.account_status === "invite_sent";
  const at = pending ? lead.invite_sent_at : lead.password_set_at;
  const tip = pending ? `Invite last sent ${at ? new Date(at).toLocaleString() : ""}` : at ? `Set up ${new Date(at).toLocaleString()}` : "Account active";
  return (
    <span data-testid={`lead-has-account-${lead.id}`} title={tip}
      className={`mt-1.5 inline-flex items-center gap-1 text-[10px] uppercase tracking-wide border px-1.5 py-0.5 whitespace-nowrap ${pending ? "border-[#F59E0B] text-[#F59E0B]" : "border-[#10B981] text-[#10B981]"}`}>
      {!pending && <UserCheck className="w-3 h-3" />}
      <span data-testid={`lead-account-status-${lead.id}`}>{pending ? "Invite Sent" : "Active"}{at ? ` · ${fmtDate(at)}` : ""}</span>
    </span>
  );
}

